import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  ActionItemType,
  ActionItemDto,
  ActionItemListDto,
  CreateActionItemDto,
  FileUploadActionTypeDto,
  LinkActionTypeDto,
  MeetingLinkActionTypeDto,
  QueryKey,
  UpdateActionItemDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { removeNullishValuesFromObject } from '@sib/api/utils';

const CACHE_TIME = 5 * 1000;
export const STEP_ACTION_TITLE_MAX_LENGTH = 256;
export const STEP_ACTION_DESCRIPTION_MAX_LENGTH = 512;
export const DEFAULT_STEP_ACTION_PAGINATION_SIZE = 10;

export type ActionTypeDto =
  | MeetingLinkActionTypeDto
  | FileUploadActionTypeDto
  | LinkActionTypeDto;

export const isFileUploadActionTypeDto = (
  actionType?: ActionTypeDto
): actionType is FileUploadActionTypeDto =>
  actionType?.type === ActionItemType.FileUpload;

export const isMeetingLinkActionTypeDto = (
  actionType?: ActionTypeDto
): actionType is MeetingLinkActionTypeDto =>
  actionType?.type === ActionItemType.MeetingLink;

export const isLinkActionTypeDto = (
  actionType?: ActionTypeDto
): actionType is LinkActionTypeDto => actionType?.type === ActionItemType.Link;

export const invalidateActionItemListQueries = (
  queryClient: QueryClient,
  actionItemId?: string
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: [
        QueryKey.Resource.InternalPortalActionItems,
        QueryKey.Operation.List,
      ],
    }),
    queryClient.invalidateQueries({
      queryKey: [
        QueryKey.Resource.InternalPortalGlobalWorkActionItems,
        QueryKey.Operation.List,
      ],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKey.Resource.ActionItem, QueryKey.Operation.List],
    }),
    ...(actionItemId
      ? [
          queryClient.invalidateQueries({
            queryKey: [QueryKey.Resource.ActionItem, actionItemId],
          }),
        ]
      : []),
  ]);

export const getActionItemInfiniteListQueryKey = (
  organizationId?: string,
  payload?: Parameters<
    typeof api.organization.actionItemControllerFindAllActionItems
  >[1]
) => [
  QueryKey.Resource.ActionItem,
  QueryKey.Operation.List,
  organizationId,
  payload,
];

export const useActionItemInfiniteList = (
  organizationId?: string,
  payload?: Parameters<
    typeof api.organization.actionItemControllerFindAllActionItems
  >[1],
  options?: Partial<
    UseInfiniteQueryOptions<
      ActionItemListDto,
      Error,
      InfiniteData<ActionItemListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: getActionItemInfiniteListQueryKey(organizationId, payload),
    queryFn: async pageParams =>
      api.organization.actionItemControllerFindAllActionItems(
        organizationId || '',
        {
          size: DEFAULT_STEP_ACTION_PAGINATION_SIZE,
          ...payload,
          page: Number(pageParams.pageParam),
        }
      ),
    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: payload?.page || DEFAULT_PAGE,
  });

export const useActionItemList = (
  organizationId: string,
  payload: Parameters<
    typeof api.organization.actionItemControllerFindAllActionItems
  >[1],
  options?: UseQueryOptions<ActionItemListDto, Error, ActionItemListDto>
) =>
  useQuery({
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    enabled: !!organizationId,
    ...options,
    queryKey: [
      QueryKey.Resource.ActionItem,
      QueryKey.Operation.List,
      organizationId,
      payload,
    ],
    queryFn: async () =>
      api.organization.actionItemControllerFindAllActionItems(
        organizationId,
        payload
      ),
  });

export const useActionItemEdit = (
  options?: UseMutationOptions<ActionItemDto, Error, UpdateActionItemDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateActionItemListQueries(queryClient, data.id);

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationSummaryActionItems,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: ({
      id,
      organizationId,
      isFileChanged,
      ...payload
    }: UpdateActionItemDto & {
      organizationId: string;
      id: string;
      isFileChanged: boolean;
    }) =>
      api.organization.actionItemControllerUpdateActionItem(
        organizationId,
        id,

        {
          assignee: payload.assignee || '',
          ...removeNullishValuesFromObject({
            action: payload.action,
            completed: payload.completed,
            description: payload.description,
            priorityLevel: payload.priorityLevel,
            spendAreaStage: payload.spendAreaStage,
            title: payload.title,
            file: isFileChanged ? payload.file : undefined,
            deadline: payload.deadline,
            notifyExternalClient: payload.notifyExternalClient,
          }),
        }
      ),
  });
};

export const useActionItemCreate = (
  options?: UseMutationOptions<
    ActionItemDto,
    Error,
    CreateActionItemDto & { organizationId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateActionItemListQueries(queryClient, data.id);
      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: (payload: CreateActionItemDto & { organizationId: string }) =>
      api.organization.actionItemControllerCreateActionItem(
        payload.organizationId,
        {
          assignee: payload.assignee || '',
          ...removeNullishValuesFromObject({
            spendAreaStage: payload.spendAreaStage,
            action: payload.action,
            description: payload.description,
            priorityLevel: payload.priorityLevel,
            title: payload.title,
            file: payload.file,
            deadline: payload.deadline,
            notifyExternalClient: payload.notifyExternalClient,
          }),
        }
      ),
  });
};

export const useActionItemOne = (
  id?: string,
  organizationId?: string,
  options?: UseQueryOptions<ActionItemDto, Error, ActionItemDto>
) =>
  useQuery({
    ...options,
    enabled: !!id && !!organizationId,
    queryKey: [QueryKey.Resource.ActionItem, id],
    queryFn: async () =>
      api.organization.actionItemControllerFindOneActionItems(
        id || '',
        organizationId || ''
      ),
  });

export interface UseActionItemDeletePayload {
  id: string;
  organizationId: string;
}

export const useActionItemDelete = (
  options?: UseMutationOptions<ActionItemDto, Error, UseActionItemDeletePayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateActionItemListQueries(queryClient, variables.id);
      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ id, organizationId }: UseActionItemDeletePayload) =>
      api.organization.actionItemControllerDeleteActionItem(id, organizationId),
  });
};
